import React, { useEffect, useMemo, useState } from 'react';

import { AppButton, AppFormLabel, AppModal, AppSelect, AppSpin } from '@/components/atoms';
import {
  useMonthlyStatementRequestForm,
  useMonthlyStatementRequestFormInit,
} from '@/hooks/main/useMonthlyStatementRequestForm';
import useAppToast from '@/hooks/useAppToast';
import type { MonthlyStatementRequestFormInitParams } from '@/services/main/rezept';
import { getApiErrorMessage } from '@/utils/apiError';

/** 「指定なし」: prints the 宛名 blank for handwriting. */
const NOT_SPECIFIED = 0;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Current search conditions; the searched massager decides the default 拠点. */
  searchParams: MonthlyStatementRequestFormInitParams;
}

/**
 * HIT-13991 — 「月別希望」 popup: pick the 拠点（施術所）printed in the 宛名 of 別紙様式5.
 * Optional (no input check); the default comes from the BE (massager's 拠点 or 指定なし).
 */
const MonthlyRequestClinicModal: React.FC<Props> = ({ isOpen, onClose, searchParams }) => {
  const toast = useAppToast();
  const { data, isFetching } = useMonthlyStatementRequestFormInit(searchParams, isOpen);
  const printMutation = useMonthlyStatementRequestForm();
  const [clinicLocationId, setClinicLocationId] = useState<number>(NOT_SPECIFIED);

  useEffect(() => {
    if (!isOpen || isFetching || !data) return;
    setClinicLocationId(data.default_clinic_location_id ?? NOT_SPECIFIED);
  }, [isOpen, isFetching, data]);

  const options = useMemo(
    () => [
      { value: NOT_SPECIFIED, label: '指定なし' },
      ...(data?.clinic_locations ?? []).map(l => ({
        value: l.id,
        label: l.serial_number != null ? `${l.serial_number}:${l.location_name}` : l.location_name,
      })),
    ],
    [data]
  );

  const handlePrint = () => {
    printMutation.mutate(clinicLocationId === NOT_SPECIFIED ? null : clinicLocationId, {
      onSuccess: onClose,
      onError: err => {
        toast.error(getApiErrorMessage(err, '月別明細書発行依頼書の印刷に失敗しました。'));
      },
    });
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="施術所選択"
      maxWidth={480}
      footer={
        <>
          <AppButton onClick={onClose} disabled={printMutation.isPending} className="rounded">
            キャンセル
          </AppButton>
          <AppButton
            type="primary"
            onClick={handlePrint}
            loading={printMutation.isPending}
            disabled={isFetching}
            className="rounded"
          >
            印刷
          </AppButton>
        </>
      }
    >
      {isFetching ? (
        <div className="flex justify-center py-4">
          <AppSpin />
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <AppFormLabel label="施術所" className="!mb-0 min-w-[64px]" />
          <AppSelect
            value={clinicLocationId}
            onChange={v => setClinicLocationId(v == null ? NOT_SPECIFIED : Number(v))}
            options={options}
            allowClear={false}
            className="flex-1"
          />
        </div>
      )}
    </AppModal>
  );
};

export default MonthlyRequestClinicModal;
